import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments';
import PracticeService from '@/services/practice-service';
import { canRetrySoulMapDocument } from '@/utils/soulmap-document-view';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';

const RETRY_FAILED_ERROR =
  'Unable to restart document generation. Please try again.';

/**
 * Restarts a failed Soul Map document from the chat header pill or the in-chat
 * card. Both share one mutation key, so a retry from either disables the other
 * and a second click never sends a duplicate request. Pending covers the POST
 * and the status refetch that follows it.
 */
export function useSoulMapDocumentRetry(
  practiceId: string,
  statusQuery: ReturnType<typeof useSoulMapDocuments>
) {
  const { data, refetch } = statusQuery;
  const queryClient = useQueryClient();
  const mutationKey = useMemo(
    () => ['soul-map-documents', practiceId, 'retry'],
    [practiceId]
  );
  const isRetrying = useIsMutating({ mutationKey }) > 0;
  const canRetry = canRetrySoulMapDocument(data);

  const { mutate } = useMutation({
    mutationKey,
    mutationFn: async () => {
      try {
        await PracticeService.retrySoulMapDocument(Number(practiceId));
      } catch (error) {
        console.error('Soul Map document retry failed:', error);
        const { data: current } = await refetch();
        // A 409 means the report is no longer retryable — usually a backend
        // retry already queued it. Show whatever status came back instead.
        const isConflict =
          axios.isAxiosError(error) && error.response?.status === 409;
        if (!isConflict || canRetrySoulMapDocument(current)) {
          toast.error(RETRY_FAILED_ERROR);
        }
        return;
      }
      await refetch();
    },
  });

  const retry = useCallback(() => {
    // Read the cache too, so a second click before the re-render is ignored.
    if (!canRetry || queryClient.isMutating({ mutationKey }) > 0) {
      return;
    }
    mutate();
  }, [canRetry, mutate, mutationKey, queryClient]);

  return { retry, canRetry, isRetrying };
}
